import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  Visibility as DisplayIcon,
  ShoppingCart as OrderIcon,
  Refresh as RefreshIcon,
  RecordVoiceOver as SuggestionIcon,
  Facebook as FacebookIcon,
  PhoneAndroid as PhoneAndroidIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

// مكونات الإعدادات
import GeneralSettings from '../../components/store/settings/GeneralSettings';
import ShippingSettingsNew from '../../components/store/settings/ShippingSettingsNew';
import PaymentSettings from '../../components/store/settings/PaymentSettings';
import NotificationSettings from '../../components/store/settings/NotificationSettings';
import DisplaySettings from '../../components/store/settings/DisplaySettings';
import OrderSettings from '../../components/store/settings/OrderSettings';
import ProductSuggestionSettings from '../../components/store/settings/ProductSuggestionSettings';
import FacebookSettings from '../../components/store/settings/FacebookSettings';
import MobileSidebarManager from '../../components/store/settings/MobileSidebarManager';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-settings-tabpanel-${index}`}
      aria-labelledby={`store-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StoreSettingsPage: React.FC = () => {
  const { settings, loading, error, refreshSettings } = useStoreSettings();

  // طباعة الإعدادات عند تغييرها
  React.useEffect(() => {
    console.log('Settings in StoreSettingsPage:', settings);
  }, [settings]);

  const [activeTab, setActiveTab] = useState(0);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // إضافة تأخير لإزالة رسالة النجاح
  const handleSaveSuccess = (success: boolean) => {
    setSaveSuccess(success);
    if (success) {
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleRefresh = async () => {
    await refreshSettings();
  };

  const handleInitializeSettings = async () => {
    console.log('Initializing store settings...');
    setSaveLoading(true);
    setSaveError(null);
    try {
      const newSettings = await refreshSettings();
      console.log('Settings initialized successfully:', newSettings);

      // إعادة تحميل الصفحة بعد تهيئة الإعدادات
      window.location.reload();

      handleSaveSuccess(true);
    } catch (err) {
      console.error('Error initializing settings:', err);
      setSaveError('حدث خطأ أثناء تهيئة الإعدادات');
    } finally {
      setSaveLoading(false);
    }
  };

  // التحقق من معلمة force في عنوان URL وتهيئة الإعدادات تلقائيًا
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const forceParam = urlParams.get('force');
    const tabParam = urlParams.get('tab');

    if (forceParam === 'true') {
      console.log('Force parameter detected, initializing settings...');
      handleInitializeSettings();

      // إزالة معلمة force من عنوان URL
      urlParams.delete('force');
      const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    }

    // تعيين علامة التبويب النشطة بناءً على معلمة tab
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 9) {
        setActiveTab(tabIndex);
      }
    }
  }, []);

  // تحديد قسم الإعدادات الحالي
  // ملاحظة: يمكن استخدام هذه الدالة لاحقًا إذا احتجنا إليها

  if (loading && !settings) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          جاري تحميل إعدادات المتجر...
        </Typography>
      </Container>
    );
  }

  if (error || !settings) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!settings && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            لم يتم العثور على إعدادات المتجر. يرجى تهيئة الإعدادات أولاً.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleRefresh}>
            إعادة المحاولة
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleInitializeSettings}
            disabled={loading || saveLoading}
          >
            تهيئة الإعدادات
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            component={RouterLink}
            to="/admin"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            لوحة التحكم
          </Link>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <StoreIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            المتجر
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            إعدادات المتجر
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            إعدادات المتجر الإلكتروني
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleInitializeSettings}
            disabled={loading || saveLoading}
            startIcon={<SettingsIcon />}
          >
            إعادة تهيئة الإعدادات
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => {
              console.log('Force reloading page...');
              window.location.href = '/admin/store-settings?tab=2&force=true';
            }}
            startIcon={<RefreshIcon />}
          >
            إعادة تهيئة إعدادات الدفع
          </Button>
        </Box>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
          تم حفظ الإعدادات بنجاح
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="store settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SettingsIcon />} label="عام" iconPosition="start" />
          <Tab icon={<ShippingIcon />} label="الشحن" iconPosition="start" />
          <Tab icon={<PaymentIcon />} label="الدفع" iconPosition="start" />
          <Tab icon={<NotificationsIcon />} label="الإشعارات" iconPosition="start" />
          <Tab icon={<DisplayIcon />} label="العرض" iconPosition="start" />
          <Tab icon={<OrderIcon />} label="الطلبات" iconPosition="start" />
          <Tab icon={<SuggestionIcon />} label="الاقتراحات" iconPosition="start" />
          <Tab icon={<FacebookIcon />} label="فيسبوك" iconPosition="start" />
          <Tab icon={<PhoneAndroidIcon />} label="القائمة الجانبية" iconPosition="start" sx={{ fontWeight: 'bold', color: 'primary.main' }} />
        </Tabs>

        {settings && (
          <>
            <TabPanel value={activeTab} index={0}>
              <GeneralSettings
                settings={settings.general}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <ShippingSettingsNew
                settings={settings.shipping}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <PaymentSettings
                settings={settings.payment}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              <NotificationSettings
                settings={settings.notification}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={4}>
              <DisplaySettings
                settings={settings.display}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={5}>
              <OrderSettings
                settings={settings.order}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={6}>
              <ProductSuggestionSettings
                settings={settings.productSuggestion}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={7}>
              <FacebookSettings
                settings={settings.general.facebookIntegration || { pageId: '', accessToken: '', isEnabled: false, autoPostNewProducts: false, postTemplate: '' }}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={8}>
              <MobileSidebarManager
                settings={settings.display}
                onSave={setSaveLoading}
                onError={setSaveError}
                onSuccess={handleSaveSuccess}
              />
            </TabPanel>
          </>
        )}

        {saveLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 2, textAlign: 'right', color: 'text.secondary' }}>
        <Typography variant="body2">
          آخر تحديث: {settings?.lastUpdated.toLocaleString('ar-EG')}
        </Typography>
      </Box>
    </Container>
  );
};

export default StoreSettingsPage;
